import {useMemo} from 'react';

import {useGetCurrentAppPackageName} from '@web-catalog/hooks/useGetCurrentAppPackageName';

import {useGetVkId} from '@web-catalog/store/auth';

import {emitYmEvent, logTmrEvent} from '@web-catalog/utils/analytics/logEvent';

export type AdPlatform = 'yandex' | 'SSP' | 'adfox';
export type AdType =
  | 'sd_banner'
  | 'floor_ad'
  | 'floor_ad_closed'
  | 'floor_ad_closed_copy'
  | 'floor_ad_banner'
  | 'sticky_banner'
  | 'sd_banner_catalog_apps'
  | 'sd_banner_side'
  | 'banner_in_featuring';

type Props = {
  platform: AdPlatform;
  adType: AdType;
};

type LogEventNames = {tmr: string; ym?: string};

export enum AdResponseType {
  HAS_ADS = '1',
  NO_ADS = '2',
}

export const useAdAnalytics = ({platform, adType}: Props) => {
  const getPackageName = useGetCurrentAppPackageName();
  const getVkId = useGetVkId();

  return useMemo(() => {
    const doLog = (
      {tmr, ym}: LogEventNames,
      slotId: string,
      position: number,
      extraParams: Record<string, unknown> | undefined,
    ) => {
      logTmrEvent({
        logEvent: tmr,
        params: {
          ...extraParams,
          platform,
          adType,
          position,
          slot_id: slotId,
          screen_app_id: getPackageName(),
          pageURL: document.location.href.split('?')[0],
          vk_id: getVkId(),
        },
      });

      if (ym) {
        emitYmEvent({eventName: ym});
      }
    };

    const logFnFactory =
      (eventNames: LogEventNames) =>
      (slotId: string, position = 0) =>
        doLog(eventNames, slotId, position, undefined);

    const parametrizedLogFnFactory =
      <T extends Record<string, unknown>>(eventNames: LogEventNames) =>
      (slotId: string, position = 0, extraParams: T) =>
        doLog(eventNames, slotId, position, extraParams);

    return {
      /** when ad is requested from platform */
      requested: logFnFactory({tmr: 'AdRequest'}),
      /** when got response from platform */
      gotResponse: parametrizedLogFnFactory<{AdResponse: AdResponseType}>({
        tmr: 'AdResponse',
      }),
      /** when ad script failed to load (e.g. due to adblock) */
      blocked: logFnFactory({tmr: 'AdRequest.blocked'}),
      /** when ad is rendered */
      rendered: logFnFactory({tmr: 'renderAdBanner'}),
      /** when ad is visible to user */
      shown: logFnFactory({tmr: 'appPage.showAd', ym: `showAd`}),
      /** when user clicks the ad */
      clicked: logFnFactory({tmr: 'appPage.clickAd', ym: `clickAd`}),
      /** when user clicks the options */
      clickedOptions: logFnFactory({tmr: 'appPage.clickAdChoices'}),
      /** when ad is closed by user interaction */
      closed: logFnFactory({tmr: 'appPage.closeAd'}),
      /** when ad provider failed to find ads to show */
      nothingToShow: logFnFactory({tmr: 'appPage.NoAd'}),
    };
  }, [platform, adType, getPackageName, getVkId]);
};
